import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { preloadImage } from "@/utils/preload-image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SwiperClass } from "swiper/react";
import { TinderCard } from "./TinderCard";

interface TinderCardSwiperProps {
  alternativeMeals: Meal[];
  mealIndex?: number;
  mealData: Meal;
  mealTime: MealTime;
  mealDay: string;
  requestId?: string | null;
  onSlideChange?: (swiper: SwiperClass) => void;
  onSwiperInit?: (swiper: SwiperClass) => void;
  onSwipeForward?: (payload: SwipeStepPayload) => void;
  onSwipeBackward?: (payload: SwipeStepPayload) => void;
  swipeMappingMode?: SwipeMappingMode;
  swipeGestureToStepMap?: {
    left: SwipeStepDirection;
    right: SwipeStepDirection;
  };
  resetSignal?: string;
}

export type SwipeStepDirection = "forward" | "backward";
type SwipeSource = "gesture" | "api";
export type SwipeMappingMode = "index" | "reverse-index" | "tinder";

const SWIPE_GESTURE_TO_STEP_MAP_PRESETS: Record<
  SwipeMappingMode,
  { left: SwipeStepDirection; right: SwipeStepDirection }
> = {
  index: { left: "forward", right: "backward" },
  "reverse-index": { left: "backward", right: "forward" },
  tinder: { left: "forward", right: "forward" },
};

interface SwipeStepPayload {
  previousIndex: number;
  nextIndex: number;
  direction: SwipeStepDirection;
  source: SwipeSource;
}

const SWIPE_THRESHOLD = 100;
const MAX_VISIBLE_CARDS = 3;
const STACK_LAYERS = [
  { y: 20, scale: 0.94, backgroundColor: "#C7C7C7" },
  { y: 10, scale: 0.97, backgroundColor: "#9D9D9D" },
];

export const TinderCardSwiper = ({
  alternativeMeals,
  mealData,
  onSlideChange,
  onSwiperInit,
  onSwipeForward,
  onSwipeBackward,
  swipeMappingMode = "index",
  swipeGestureToStepMap,
  resetSignal,
}: TinderCardSwiperProps) => {
  const preloadedImageUrisRef = useRef<Set<string>>(new Set());
  const currentIndexRef = useRef(0);
  const previousResetSignalRef = useRef<string | undefined>(undefined);

  const resolvedSwipeGestureToStepMap = useMemo(
    () => swipeGestureToStepMap ?? SWIPE_GESTURE_TO_STEP_MAP_PRESETS[swipeMappingMode],
    [swipeGestureToStepMap, swipeMappingMode],
  );

  const slides = useMemo(() => {
    if (alternativeMeals.length > 0) {
      return alternativeMeals;
    }
    return [mealData];
  }, [alternativeMeals, mealData]);

  const initialIndex = useMemo(() => {
    const storedIndexString =
      typeof window === "undefined"
        ? null
        : window.localStorage.getItem(LocalStorageKeys.SWAP_PLAN_MEAL_INDEX);
    const storedIndex = storedIndexString ? parseInt(storedIndexString, 10) : undefined;
    if (storedIndex !== undefined && !Number.isNaN(storedIndex)) {
      return Math.min(Math.max(storedIndex, 0), slides.length - 1);
    }
    return 0;
  }, [slides.length]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  currentIndexRef.current = currentIndex;

  const runStep = useCallback(
    (direction: SwipeStepDirection, source: SwipeSource) => {
      const previousIndex = currentIndexRef.current;
      const nextIndex =
        direction === "forward"
          ? Math.min(previousIndex + 1, slides.length - 1)
          : Math.max(previousIndex - 1, 0);

      if (nextIndex === previousIndex) {
        return;
      }

      currentIndexRef.current = nextIndex;
      setCurrentIndex(nextIndex);

      const payload: SwipeStepPayload = { previousIndex, nextIndex, direction, source };
      if (direction === "forward") {
        onSwipeForward?.(payload);
        return;
      }
      onSwipeBackward?.(payload);
    },
    [slides.length, onSwipeForward, onSwipeBackward],
  );

  const createMockSwiper = useCallback(
    (activeIndex: number): SwiperClass => {
      return {
        activeIndex,
        slideTo: (index: number) => {
          const clampedIndex = Math.min(Math.max(index, 0), slides.length - 1);
          const previousIndex = currentIndexRef.current;
          if (clampedIndex === previousIndex) {
            return;
          }

          currentIndexRef.current = clampedIndex;
          setCurrentIndex(clampedIndex);

          const direction: SwipeStepDirection =
            clampedIndex > previousIndex ? "forward" : "backward";
          const payload: SwipeStepPayload = {
            previousIndex,
            nextIndex: clampedIndex,
            direction,
            source: "api",
          };
          if (direction === "forward") {
            onSwipeForward?.(payload);
            return;
          }
          onSwipeBackward?.(payload);
        },
        slideNext: () => {
          runStep("forward", "api");
        },
        slidePrev: () => {
          runStep("backward", "api");
        },
      } as SwiperClass;
    },
    [slides.length, runStep, onSwipeForward, onSwipeBackward],
  );

  useEffect(() => {
    onSwiperInit?.(createMockSwiper(currentIndex));
  }, [onSwiperInit, createMockSwiper, currentIndex]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LocalStorageKeys.SWAP_PLAN_MEAL_INDEX, currentIndex.toString());
    }
    onSlideChange?.(createMockSwiper(currentIndex));
  }, [currentIndex, onSlideChange, createMockSwiper]);

  useEffect(() => {
    setCurrentIndex((previousIndex) => Math.min(previousIndex, slides.length - 1));
  }, [slides.length]);

  useEffect(() => {
    if (previousResetSignalRef.current === undefined) {
      previousResetSignalRef.current = resetSignal;
      return;
    }
    if (previousResetSignalRef.current === resetSignal) {
      return;
    }

    previousResetSignalRef.current = resetSignal;
    currentIndexRef.current = 0;
    setCurrentIndex(0);
  }, [resetSignal]);

  const preloadRange = useCallback(
    (start: number, end: number) => {
      if (!slides.length) {
        return;
      }

      const clampedStart = Math.max(0, start);
      const clampedEnd = Math.min(slides.length - 1, end);

      if (clampedStart > clampedEnd) {
        return;
      }

      for (let index = clampedStart; index <= clampedEnd; index += 1) {
        const imageUri = slides[index]?.imageUri;
        if (!imageUri || preloadedImageUrisRef.current.has(imageUri)) {
          continue;
        }

        preloadedImageUrisRef.current.add(imageUri);
        preloadImage(imageUri);
      }
    },
    [slides],
  );

  useEffect(() => {
    preloadedImageUrisRef.current.clear();
    preloadRange(currentIndex, currentIndex + MAX_VISIBLE_CARDS);
  }, [slides, currentIndex, preloadRange]);

  const canSwipeForward = currentIndex < slides.length - 1;
  const canSwipeBackward = currentIndex > 0;

  const canSwipeLeft =
    resolvedSwipeGestureToStepMap.left === "forward" ? canSwipeForward : canSwipeBackward;
  const canSwipeRight =
    resolvedSwipeGestureToStepMap.right === "forward" ? canSwipeForward : canSwipeBackward;

  const handleSwipe = useCallback(
    (gesture: "left" | "right") => {
      const stepDirection = resolvedSwipeGestureToStepMap[gesture];
      runStep(stepDirection, "gesture");
    },
    [resolvedSwipeGestureToStepMap, runStep],
  );

  const visibleSlides = useMemo(() => {
    return slides.slice(currentIndex, currentIndex + MAX_VISIBLE_CARDS);
  }, [slides, currentIndex]);

  const activeMeal = visibleSlides[0];
  const shadowLayerCount = Math.min(STACK_LAYERS.length, Math.max(visibleSlides.length - 1, 0));

  if (slides.length === 0) {
    return (
      <div className="flex w-full flex-1 items-center justify-center">
        <div className="text-center text-lg font-semibold text-gray-500">
          No meals found for this meal time
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full w-full flex-1 items-center justify-center overflow-hidden px-4">
      <div className="relative aspect-square w-full">
        {Array.from({ length: shadowLayerCount }).map((_, index) => {
          const layer = STACK_LAYERS[index];
          return (
            <div
              key={`shadow-layer-${index}`}
              className="absolute inset-0 rounded-2xl"
              style={{
                transform: `translateY(${layer.y}px) scale(${layer.scale})`,
                zIndex: index + 1,
                backgroundColor: layer.backgroundColor,
              }}
            />
          );
        })}
        {activeMeal && (
          <TinderCard
            key={activeMeal.id ?? `${activeMeal.name}-${currentIndex}`}
            meal={activeMeal}
            swipeThreshold={SWIPE_THRESHOLD}
            canSwipeLeft={canSwipeLeft}
            canSwipeRight={canSwipeRight}
            onSwipe={handleSwipe}
          />
        )}
      </div>
    </div>
  );
};
